import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, authClient, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import SectionHeader from "@/components/SectionHeader";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Upload, ImageIcon, X, Sparkles } from "lucide-react";
import { sfx } from "@/lib/sounds";

const SOCIAL_FIELDS = [
  ["youtube", "YouTube channel URL"],
  ["twitch", "Twitch channel URL"],
  ["kick", "Kick channel URL"],
  ["instagram", "Instagram"],
  ["twitter", "X (Twitter)"],
  ["facebook", "Facebook"],
  ["tiktok", "TikTok"],
  ["discord", "Discord username"],
  ["website", "Website"],
  ["github", "GitHub"],
  ["other", "Other social link"],
];

async function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}
function downscale(dataUrl, maxWidth = 1600) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      if (img.width <= maxWidth) return resolve(dataUrl);
      const ratio = maxWidth / img.width;
      const canvas = document.createElement("canvas");
      canvas.width = maxWidth; canvas.height = Math.round(img.height * ratio);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export default function CreatorApply() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    full_name: "", minecraft_username: "", email: "", date_of_birth: "",
    country: "India", preferred_language: "English",
    about: "", why_creator: "", audience_size: "",
    banner_image: "", profile_picture: "",
    social: {},
  });
  const [busy, setBusy] = useState(false);
  const bannerRef = useRef(null);
  const avatarRef = useRef(null);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    setForm((f) => ({
      ...f,
      full_name: user.name || "",
      minecraft_username: user.mc_username || "",
      email: user.email || "",
      date_of_birth: user.date_of_birth || "",
      country: user.country || "India",
      social: user.social || {},
    }));
    authClient.get("/creators/application/me").then((r) => setStatus(r.data)).catch(() => {});
  }, [user]);

  const pickImage = async (kind) => {
    const inp = kind === "banner" ? bannerRef.current : avatarRef.current;
    const file = inp.files?.[0];
    if (!file) return;
    const raw = await fileToDataUrl(file);
    const small = await downscale(raw, kind === "banner" ? 1600 : 512);
    setForm({ ...form, [kind === "banner" ? "banner_image" : "profile_picture"]: small });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.minecraft_username || !form.email) {
      sfx.error(); return toast.error("Full name, Minecraft username and email are required");
    }
    setBusy(true);
    try {
      const { data } = await authClient.post("/creators/apply", form);
      sfx.levelup();
      toast.success("Application submitted!", { description: "We'll review within 3-5 days." });
      setStatus(data.application);
    } catch (err) {
      sfx.error(); toast.error(formatApiError(err));
    }
    setBusy(false);
  };

  if (!user) return null;

  // Already submitted view
  if (status && status.status !== "rejected") {
    const badgeColor = { pending: "#FFBF00", under_review: "#3ABEFF", approved: "#22c55e" }[status.status] || "#FFBF00";
    return (
      <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="creator-status">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#FFBF00]/15 grid place-items-center text-[#FFBF00] mb-4">
            <Sparkles size={26} />
          </div>
          <div className="text-[11px] uppercase tracking-[0.4em] text-[#FFBF00]">Creator Application</div>
          <div className="font-hero text-4xl mt-2">
            {status.status === "approved" ? "You're a Creator!" : "Application Received"}
          </div>
          <p className="text-white/60 mt-3 max-w-md mx-auto">
            {status.status === "approved"
              ? "Welcome to the LostDC Creator Program. Your profile now has the Creator badge."
              : "Our team is reviewing your submission. You'll be notified once a decision is made."}
          </p>
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full glass" style={{ borderColor: `${badgeColor}55` }}>
            <span className="w-2 h-2 rounded-full" style={{ background: badgeColor }} />
            <span className="text-xs uppercase tracking-widest font-mono" style={{ color: badgeColor }}>{status.status.replace("_", " ")}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-creator-apply">
      <div className="max-w-3xl mx-auto">
        <SectionHeader
          eyebrow="Creator Program"
          title="Apply to Be a Creator"
          subtitle="Join the LostDC Creator Program — get a badge, feature on the homepage, YouTube integration and more."
        />

        <form onSubmit={submit} className="rounded-2xl glass p-6 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Full Name *" value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} testId="ca-fullname" />
            <Field label="Minecraft Username *" value={form.minecraft_username} onChange={(v) => setForm({ ...form, minecraft_username: v })} testId="ca-mc" />
            <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
            <Field label="Date of Birth" type="date" value={form.date_of_birth} onChange={(v) => setForm({ ...form, date_of_birth: v })} />
            <Field label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
            <Field label="Preferred Language" value={form.preferred_language} onChange={(v) => setForm({ ...form, preferred_language: v })} />
            <Field label="Estimated Audience Size" value={form.audience_size} onChange={(v) => setForm({ ...form, audience_size: v })} placeholder="e.g. 5,000 subscribers" span={2} />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FFBF00] mb-3">About You</div>
            <Area label="About Yourself" value={form.about} onChange={(v) => setForm({ ...form, about: v })} testId="ca-about" />
            <Area label="Why do you want to become a LostDC Creator?" value={form.why_creator} onChange={(v) => setForm({ ...form, why_creator: v })} testId="ca-why" />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FFBF00] mb-3">Social Links</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {SOCIAL_FIELDS.map(([k, label]) => (
                <Field key={k} label={label} value={form.social[k] || ""} onChange={(v) => setForm({ ...form, social: { ...form.social, [k]: v } })} />
              ))}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#FFBF00] mb-3">Media</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <MediaPicker
                label="Banner Image (16:6)" testId="ca-banner"
                image={form.banner_image}
                onClear={() => setForm({ ...form, banner_image: "" })}
                onPick={() => bannerRef.current?.click()}
                inputRef={bannerRef}
                onFile={() => pickImage("banner")}
                aspect="aspect-[16/6]"
              />
              <MediaPicker
                label="Profile Picture" testId="ca-avatar"
                image={form.profile_picture}
                onClear={() => setForm({ ...form, profile_picture: "" })}
                onPick={() => avatarRef.current?.click()}
                inputRef={avatarRef}
                onFile={() => pickImage("avatar")}
                aspect="aspect-square"
              />
            </div>
          </div>

          <button
            type="submit" disabled={busy} data-testid="ca-submit"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#FFBF00] text-black font-semibold uppercase tracking-wider hover:bg-[#FF8C00] transition-colors disabled:opacity-60"
          >
            <Upload size={16} /> {busy ? "Submitting..." : "Submit Application"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", span = 1, testId }) {
  return (
    <label className={`block ${span === 2 ? "sm:col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">{label}</span>
      <Input data-testid={testId} type={type} value={value} placeholder={placeholder}
             onChange={(e) => onChange(e.target.value)}
             className="bg-black/60 border-white/10 text-white" />
    </label>
  );
}
function Area({ label, value, onChange, testId }) {
  return (
    <label className="block mb-3">
      <span className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">{label}</span>
      <Textarea data-testid={testId} rows={4} value={value} onChange={(e) => onChange(e.target.value)}
                className="bg-black/60 border-white/10 text-white" />
    </label>
  );
}
function MediaPicker({ label, image, onPick, onClear, inputRef, onFile, aspect, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-white/50 block mb-1">{label}</div>
      <button type="button" onClick={onPick} data-testid={testId}
              className={`w-full ${aspect} rounded-lg border-2 border-dashed border-white/15 hover:border-[#FFBF00]/50 overflow-hidden relative`}>
        {image ? (
          <>
            <img src={image} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={(e) => { e.stopPropagation(); onClear(); }}
                    className="absolute top-2 right-2 p-1 rounded-full bg-black/70 hover:bg-red-500/70">
              <X size={12} />
            </button>
          </>
        ) : (
          <div className="w-full h-full grid place-items-center text-white/50">
            <ImageIcon size={22} />
          </div>
        )}
      </button>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} />
    </div>
  );
}
